import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getAllComponentListLCA,
  addAllComponentToProductLCA,
  updateAllComponentLCA,
  deleteAllComponentLCA,
} from "../../../../../../apis/Product/ComponentApi";
import { getProdComponentDropdown } from "../../../../../../apis/Dropdown";
import { getBSDDDictionaryClassesDropdown } from "../../../../../../apis/OpenCircApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import ImpactCategoryHeader from "../../../../../CustomHeadlessTable/components/ImpactCategoryHeader";
import EmissionsgrundlagPanel from "./EmissionsgrundlagPanel";
// Impact categories configuration - All 16 categories
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  {
    name: "Photochemical ozone formation",
    unit: "kg NMVOC-eq",
    key: "smog",
  },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  {
    name: "Eutrophication, marine",
    unit: "kg N-eq",
    key: "eutrophication_marine",
  },
  {
    name: "Human toxicity, cancer effects",
    unit: "CTUh",
    key: "carcinogenics",
  },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  {
    name: "Particulate matter",
    unit: "disease incidence",
    key: "respiratory_effects",
  },
  {
    name: "Ecotoxicity, freshwater",
    unit: "CTUe",
    key: "ecotoxicity",
  },
  {
    name: "Resource use, energy carriers",
    unit: "MJ",
    key: "fossil_fuel_depletion",
  },
  { name: "Land use", unit: "Pt", key: "land_use" },
  {
    name: "Water use",
    unit: "m³ world-eq deprived",
    key: "water_use",
  },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const ProdComponentTable = ({ productId, subcategoryId, isSubcategory, onSyncComplete }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});
  const [componentDropdown, setComponentDropdown] = useState([]);
  const [bsddDropdown, setBsddDropdown] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] =
    useState(0);
  const [selectedComponentForPanel, setSelectedComponentForPanel] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  // Fetch component dropdown data (database)
  useEffect(() => {
    getProdComponentDropdown({
      setDropdown: setComponentDropdown,
    });
  }, []);

  // Always fetch bSDD dropdown data (using OpenCirc)
  useEffect(() => {
    getBSDDDictionaryClassesDropdown({
      dictionary: "table6", // BR18 Table 6 dictionary
      search: "",
      setDropdown: setBsddDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    // Removed subcategory support - now always use getAllComponentList
    // if (productId && subcategoryId && isSubcategory) {
    //   getComponentList({
    //     productId,
    //     subcategoryId,
    //     setObjects: setData,
    //   });
    // } else if (!isSubcategory) {
    if (productId) {
      getAllComponentListLCA({
        productId,
        setObjects: setData,
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Listen for sync complete event
  useEffect(() => {
    const handleSyncComplete = () => {
      // Refresh data to get updated dataset states
      fetchData();
    };

    window.addEventListener("opencirc-sync-complete", handleSyncComplete);

    return () => {
      window.removeEventListener("opencirc-sync-complete", handleSyncComplete);
    };
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      // Always use database dropdown for unit lookup (calculations use database component)
      data.forEach((row) => {
        // Backend returns component as code string (e.g., "MAA_LEATHERVEG_C16EAC81061F")
        // Can also be an object with value property from dropdown selection
        const componentCode =
          typeof row.component === "string"
            ? row.component
            : row.component?.value ||
              row.component ||
              row.input ||
              (typeof row.input === "string" ? row.input : row.input?.value);
        if (componentCode) {
          // Find component by code in database dropdown (for calculations)
          const selectedComponent = componentDropdown.find(
            (option) =>
              !option.isCategory &&
              (option.code === componentCode ||
                option.value === componentCode ||
                option.name === componentCode)
          );
          if (selectedComponent) {
            newUnitMaterials[row.id] = selectedComponent.unit || "kg";
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, componentDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      component: {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedComponent = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedComponent) {
            const uom = selectedComponent.unit || "kg";
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({ ...prev, [rowId]: uom }));
            } else {
              setUnitMaterials((prev) => ({ ...prev, new: uom }));
            }
          }
        },
        dropdownDataKey: "component",
      },
    }),
    []
  );
  const handleEdit = useCallback(
    (editedItem) => {
      // Get database component code (for calculations)
      const componentValue =
        editedItem.component?.value ||
        editedItem.component ||
        editedItem.input?.value ||
        editedItem.input;

      // Find component in database dropdown to get code
      const foundComponent = componentDropdown?.find(
        (option) =>
          !option.isCategory &&
          (option.value === componentValue ||
            option.code === componentValue ||
            option.name === componentValue)
      );

      // Use code field for API (backend expects component code)
      const componentCode = foundComponent?.code || componentValue;
      const product_code = foundComponent?.code || editedItem.product_code;

      // Get bSDD component code (from description field which is now bSDD dropdown)
      const bsddValue =
        editedItem.description?.value ||
        editedItem.description ||
        editedItem.bsdd_component;

      // Find bSDD component in bsddDropdown
      const foundBSDDComponent = bsddDropdown?.find(
        (option) =>
          !option.isCategory &&
          (option.value === bsddValue ||
            option.code === bsddValue ||
            option.name === bsddValue)
      );

      const bsddComponentCode = foundBSDDComponent?.code || foundBSDDComponent?.value || bsddValue;

        updateAllComponentLCA({
          productId,
          componentId: editedItem.id,
          ...editedItem,
        component: componentCode, // Database component (for calculations)
        bsdd_component: bsddComponentCode, // bSDD component (for reference)
        description: bsddComponentCode, // Keep description field for backward compatibility
        qty: editedItem.qty,
        supplier: editedItem.supplier,
        input: componentCode, // Keep for backward compatibility
          objects: data,
          setObjects: setData,
          use_swal: true,
          product_code: product_code,
        });
    },
    [productId, data, setData, componentDropdown, bsddDropdown]
  );

  const handleAdd = useCallback(
    (newItem) => {
      // Get database component code (for calculations)
      const componentValue =
        newItem.component?.value ||
        newItem.component ||
        newItem.input?.value ||
        newItem.input;

      // Find component in database dropdown to get code
      const foundComponent = componentDropdown?.find(
        (option) =>
          !option.isCategory &&
          (option.value === componentValue ||
            option.code === componentValue ||
            option.name === componentValue)
      );

      // Use code field for API (backend expects component code)
      const componentCode = foundComponent?.code || componentValue;
      const product_code = foundComponent?.code || "";

      // Get bSDD component code (from description field which is now bSDD dropdown)
      const bsddValue =
        newItem.description?.value ||
        newItem.description ||
        newItem.bsdd_component;

      // Find bSDD component in bsddDropdown
      const foundBSDDComponent = bsddDropdown?.find(
        (option) =>
          !option.isCategory &&
          (option.value === bsddValue ||
            option.code === bsddValue ||
            option.name === bsddValue)
      );

      const bsddComponentCode = foundBSDDComponent?.code || foundBSDDComponent?.value || bsddValue;

        addAllComponentToProductLCA({
          productId,
        component: componentCode, // Database component (for calculations)
        bsdd_component: bsddComponentCode, // bSDD component (for reference)
        description: bsddComponentCode, // Keep description field for backward compatibility
        qty: newItem.qty,
        supplier: newItem.supplier,
        input: componentCode, // Keep for backward compatibility
          callback: fetchData,
          use_swal: true,
          product_code: product_code,
        });
    },
    [productId, fetchData, componentDropdown, bsddDropdown]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      // Removed subcategory support
      // if (isSubcategory) {
      //   deleteComponent({
      //     productId,
      //     subcategoryId: subcategoryId,
      //     componentId: itemToDelete.id,
      //     callback: fetchData,
      //   });
      // } else {
        deleteAllComponentLCA({
          productId,
          componentId: itemToDelete.id,
          callback: fetchData,
        });
      // }
    },
    [productId, fetchData]
  );

  // Navigation handlers for impact categories
  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev > 0 ? prev - 1 : IMPACT_CATEGORIES.length - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev < IMPACT_CATEGORIES.length - 1 ? prev + 1 : 0
  );
  }, []);


  const handleDatabaseIconClick = useCallback((component) => {
    if (!component) {
      return;
    }
    // Create a fresh copy to avoid reference issues
    setSelectedComponentForPanel({ ...component });
    setPanelOpen(true);
  }, []);

  const handleDatasetActivated = useCallback((updatedComponent) => {
    // Update component in table
    setData((prevData) =>
      prevData.map((item) =>
        item.id === updatedComponent.id ? { ...item, ...updatedComponent } : item
      )
    );
    setPanelOpen(false);
    fetchData(); // Refresh to get updated totals
  }, [fetchData]);

  const handleDatasetRejected = useCallback((updatedComponent) => {
    // Update component in table
    setData((prevData) =>
      prevData.map((item) =>
        item.id === updatedComponent.id ? { ...item, ...updatedComponent } : item
      )
    );
    setPanelOpen(false);
  }, []);

  const columns = useMemo(() => {
    const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
    // Backend returns impact categories directly (e.g., global_warming), not nested
    const impactCategoryAccessor = currentImpactCategory.key;

    return [
      {
        accessor: "description",
        header:  `${t("COMPONENT")} (table6)`,
        type: "dropdown",
        isCategory: true,
        dropdownData: bsddDropdown,
        disableSortBy: true,
        placeholder: t("USER_TYPES") || "user types...",
        width: "200px",
        optional: true, // bSDD component is optional
      },
      {
        accessor: "component",
        header: `${t("COMPONENT") || "Component"}*`,
        type: "dropdown",
        isCategory: true,
        dropdownData: componentDropdown,
        disableSortBy: true,
        width: "200px",
        unit: (rowId, rowData) => {
          const componentCode =
            typeof rowData?.component === "string"
              ? rowData.component
              : rowData?.component?.value ||
                rowData?.component ||
                rowData?.input ||
                (typeof rowData?.input === "string"
                  ? rowData.input
                  : rowData?.input?.value);
          if (componentCode) {
            const foundComponent = componentDropdown.find(
              (option) =>
                !option.isCategory &&
                (option.code === componentCode ||
                  option.value === componentCode ||
                  option.name === componentCode)
            );
            return foundComponent?.unit || unitMaterials[rowId] || "kg";
          }
          return unitMaterials[rowId] || "kg";
        },
      },
      // Temporarily commented out - mint dot indicator column
      // {
      //   id: "opencirc_indicator",
      //   accessor: "opencirc_indicator",
      //   header: "",
      //   type: "indicator",
      //   disableSortBy: true,
      //   width: "60px",
      //   onIndicatorClick: handleDatabaseIconClick,
      //   optional: true
      // },
      {
        accessor: "qty",
        header: `${t("QTY_FUNC_UNIT") || "Quantity per functional unit"}*`,
        type: "number",
        tipHeader: "Tip",
        tipBody: t("TIP_QTY_FUNC_COMP"),
        disableSortBy: true,
        placeholder: t("USER_TYPES") || "user types...",
        width: "180px",
        unit: (rowId, rowData) => {
          // Backend returns component as code string (e.g., "MAA_LEATHERVEG_C16EAC81061F")
          // Can also be an object with value property from dropdown selection
          const componentCode =
            typeof rowData?.component === "string"
              ? rowData.component
              : rowData?.component?.value ||
                rowData?.component ||
                rowData?.input ||
                (typeof rowData?.input === "string"
                  ? rowData.input
                  : rowData?.input?.value);
          if (componentCode) {
            const foundComponent = componentDropdown.find(
              (option) =>
                !option.isCategory &&
                (option.code === componentCode ||
                  option.value === componentCode ||
                  option.name === componentCode)
            );
            return foundComponent?.unit || unitMaterials[rowId] || "kg";
          }
          return unitMaterials[rowId] || "kg";
        },
      },
      {
        accessor: "supplier",
        header: t("SUPPLIER") || "Supplier",
        type: "text",
        disableSortBy: true,
        placeholder: t("USER_TYPES") || "user types...",
        width: "150px",
      },
      {
        accessor: impactCategoryAccessor,
        header: () => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          return (
            <ImpactCategoryHeader
              categoryName={currentCategory.name}
              categoryUnit={currentCategory.unit}
              onPrevious={handlePreviousImpactCategory}
              onNext={handleNextImpactCategory}
              infoTitle={currentCategory.name}
              infoMessage={`Impact category: ${currentCategory.name}. Unit: ${
                currentCategory.unit || "N/A"
              }`}
            />
          );
        },
        type: "custom",
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          // Backend returns impact categories directly on the object (e.g., global_warming, not impact_global_warming)
          const impactValue =
            row.original[currentCategory.key] || // Direct field from API (e.g., global_warming)
            row.original[`impact_${currentCategory.key}`] || // Fallback for nested structure
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        optional: true,
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ];
  }, [
    componentDropdown,
    bsddDropdown,
    t,
    unitMaterials,
    currentImpactCategoryIndex,
    handlePreviousImpactCategory,
    handleNextImpactCategory,
    handleDatabaseIconClick,
  ]);

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  // Backend returns impact categories directly (e.g., global_warming), not nested
  const impactCategoryAccessor = currentImpactCategory.key;

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: {
        label: `Total ${currentImpactCategory.name}`,
      },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      component: componentDropdown, // Database dropdown for calculations
      description: bsddDropdown, // bSDD dropdown for reference
    }),
    [componentDropdown, bsddDropdown]
  );

  return (
    <>
      <CustomTable
        columns={columns}
        data={data}
        setData={setData}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        dropdownData={memoizedDropdownData}
        subtotalColumns={subtotalColumns}
        customInputs={useMemo(
          () => ({ [impactCategoryAccessor]: CustomInputComponent }),
          [impactCategoryAccessor]
        )}
        hiddenColumns={hiddenColumns}
        toggleColumnVisibility={toggleColumnVisibility}
        disableRules={disableRules}
        dynamicPlaceholders={dynamicPlaceholders}
        comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      />
      <EmissionsgrundlagPanel
        component={selectedComponentForPanel}
        productId={productId}
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        onDatasetActivated={handleDatasetActivated}
        onDatasetRejected={handleDatasetRejected}
        componentDropdown={componentDropdown}
        bsddDropdown={bsddDropdown}
      />
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.6;
            }
          }
        `}
      </style>
    </>
  );
};

export default ProdComponentTable;
